import React, { useRef, useState, useEffect, useCallback } from "react";
import io from "socket.io-client";
import { Route, Routes } from "react-router-dom";
import AlertView from "./routes/AlertView";
import MainView from "./routes/MainView";
import StatusView from "./routes/StatusView";
import useConfig from "./features/config/useConfig";
import Header from "./features/header/Header";
import Debug from "./features/debugging.js"
import { adptRestEndpoint, activeDECsResponseParser } from "./features/ats-adpt-rest.js"
import * as Diag from "./features/diagnostics.js"
import { useInterval } from "./hooks/useInterval"
import { useRestPolling, ResponseParsers } from "./hooks/useRestPolling"
import { useDebounceWithMaxDelay } from "./hooks/useDebounceWithMaxDelay"

export const UserContext = React.createContext("");

function App() {
  const {config, configLoading, configError} = useConfig();
  const [socket, setSocket] = useState(null);
  const [socketIsConnected, setSocketIsConnected] = useState(false);
  const [socketLostConnection, setSocketLostConnection] = useState(false);
  const [disk, setDiskData] = useState();
  const [messageData, setMessageData] = useState([]);
  const [timer, setTimer] = useState("00:00:00");
  const [id, setIntervalId] = useState(0);
  const [buttonstr, setButtonstr] = useState("Record");
  const [isrecording, setRecordingStatus] = useState(false);
  const [messageDataLength, setMessageDataLength] = useState(0);
  const [alertMessages, setAlertMessages] = useState([]);
  const [cameraStatusMessage, setCameraStatusMessages] = useState([]);
  const [lidarStatus, setLidarStatus] = useState(null);
  const [videoInputStatus, setVideoInputStatus] = useState(null);
  const [diagnosticTests, setDiagnosticTests] = useState(Diag.createDiagnosticTests);
  const [activeDiagCodes, setActiveDiagCodes] = useState(null);
  const [reportedMetrics, setReportedMetrics] = useState({});
  const [metricWhitelist, setMetricWhitelist] = useState(() => new Map());
  const [vehicleParameters, setVehicleParameters] = useState(null);
  const [lastSuccessfulRestPoll, setLastSuccessfulRestPoll] = useState(() => ({
      pcu_a: Number.MAX_VALUE, // Avoids false positives toast messages before...
      pcu_b: Number.MAX_VALUE, // ... a successful request has happened.
  }));

  const updateRestPollTimouts = (host) =>
      setLastSuccessfulRestPoll(prev => ({ ...prev, [host]: Date.now() }));

  const updateReportedMetrics = (metrics) =>
    setReportedMetrics(prev => {

      let metricChanged = false;
      const filtered = {};

      for (const key in metrics) {
        if (metricWhitelist.has(key) && metrics[key] !== prev[key]) {
          filtered[key] = metrics[key];
          metricChanged = true;
        }
      }

      return metricChanged ? {...prev, ...filtered} : prev;
    });

  const mergeStatusUpdates = (prevState, newState) => ({
    ...prevState,
    data: { ...prevState?.data, ...newState?.data },
    messages: [
      {
        ...prevState?.messages?.[0],
        ...newState?.messages?.[0],
      },
    ],
    timestamp: newState.timestamp || prevState?.timestamp,
  });

  useEffect(() => {
      if (config) {
        Debug.enabled |= config.debug_enabled;
        Debug.logGroup("Config loaded", config);
      }
  }, [config]);

  useEffect(() => {
    const diskCapacityListener = (disk) => {
      setDiskData(disk);
    };

    const messageListener = (message) => {
      setMessageData((prevState) => {
        const res = prevState.some((item) => item.name === message.name);

        if (!res) {
          return [...prevState, message];
        }

        return prevState.map((value) =>
          value.name === message.name ? message : value
        );
      });

      if (message.name === "Video_input") {
        setCameraStatusMessages(message.messages);
      }
    };

    if (socket === null) return;

    socket.on("connect", () => {
      setSocketIsConnected(true);
      setAlertMessages(prevMessages => prevMessages.filter(Diag.isDiagnosticsAlert));
      setSocketLostConnection(false);
    });

    socket.on("disconnect", () => {
      setSocketLostConnection(true);
    });

    socket.on("reconnect", () => {
      setSocketLostConnection(false);
    });

    socket.on("MONITOR.STATUS", messageListener);
    socket.on("STATUS.DIRECTORY", diskCapacityListener);

    return () => {
      socket.off("MONITOR.STATUS", messageListener);
      socket.off("STATUS.DIRECTORY", diskCapacityListener);
    };
  }, [socket]);

  useEffect(() => {
    if (socket === null) return;

    const handleMonitorStatus = (data) => {
      if (data.name === "Lidar") {
        setLidarStatus((prevState) => mergeStatusUpdates(prevState, data));
      } else if (data.name === "Video_input") {
        setVideoInputStatus((prevState) => mergeStatusUpdates(prevState, data));
      }
    };

    socket.on("MONITOR.STATUS", handleMonitorStatus);

    return () => {
      socket.off("MONITOR.STATUS", handleMonitorStatus);
    };
  }, [socket]);

  useEffect(() => {
    messageData.forEach((message) => {
      const isMessageInAlertList = alertMessages.some(
        (item) => item.name === message.name
      );

      if (message.status && isMessageInAlertList) {
        setAlertMessages((prevState) =>
          prevState.filter((msg) => msg.name !== message.name)
        );
      } else if (!message.status && !isMessageInAlertList) {
        setAlertMessages((prevState) => [...prevState, message]);
      }
    });
  }, [messageData, alertMessages]);

  useEffect(() => {
    const newSocket = io(window.location.origin, { path: "/socket.io" });
    setSocket(newSocket);
    return () => newSocket.close();
  }, []);

  useEffect(() => {
    if (alertMessages) {
      setMessageDataLength(alertMessages.length);
    }
  }, [alertMessages]);

  // NOTE(abezqk, 2026-06-10):
  //  I have chosen quite arbitrary times for polling the ats-adpt-rest APIs.
  //  I tried to space them out to reduce network spikes, but that also means
  //  that the tests will retrigger often. Someone should probably investigate
  //  if the data should be polled in a short interval, and that the tests are
  //  executed after that. Maybe it all should be controlled by time, instead
  //  of the state dependencies currently used.

  //
  // Poll the active diagnostics periodically.
  //

  const updateDiagnostics = (decs) => {
    updateRestPollTimouts("pcu_b");
    setActiveDiagCodes(new Set(decs.map(dec => dec.code)));
  };

  useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_b, "diagnostics/dec/active"),
    parser: activeDECsResponseParser,
    handler: updateDiagnostics,
    delay: 3000,
    interval: 2000, // Note: DECs from ATS apps are polled by the framework every ~3s
  });

  //
  // Run diagnostics tests whenever the DECs, metrics or tests are updated.
  // Triggered tests appends stubbed alert messages for the Alert view.
  // Any missing metrics reported are added to the metrics whitelist.
  //

  const updateMetricWhitelist = (metrics) => {
    setMetricWhitelist((prev) => {
      let changed = false;

      const next = new Map(prev);

      for (const metric of metrics) {
        if (!next.has(metric)) {
          next.set(metric, Date.now());
          changed = true;
        }
      }
      
      return changed ? next : prev;
    });
  };

  const updateAlertMessages = (newlyTriggered, newlyRecovered, timestamp) => {
    setAlertMessages(prevMessages =>
      Diag.updateDiagnosticsAlerts(prevMessages, newlyTriggered, newlyRecovered, timestamp)
    );
  };

  const prevTriggeredTests = useRef([]);

  const runDiagnosticsTests = useCallback(() => {

    if (!diagnosticTests || activeDiagCodes === null) {
      return;
    }

    const timestampNs = Date.now() * 1_000_000;

    const missingMetrics = Diag.runTests(
      diagnosticTests,
      activeDiagCodes,
      reportedMetrics,
      timestampNs
    );

    const currTriggered = diagnosticTests.filter(test => test.triggered);
    const prevTriggered = prevTriggeredTests.current;
    prevTriggeredTests.current = currTriggered;

    const newlyTriggered = currTriggered.filter(test => !prevTriggered.includes(test));
    const newlyRecovered = prevTriggered.filter(test => !currTriggered.includes(test));

    if (missingMetrics.size !== 0) {
      updateMetricWhitelist(missingMetrics);
    }

    if (newlyTriggered.length !== 0 || newlyRecovered.length !== 0) {
      updateAlertMessages(newlyTriggered, newlyRecovered, timestampNs);
    }

    Debug.logGroup("Diagnostic tests run.", {
        DECs: activeDiagCodes,
        Metrics: reportedMetrics,
        Tests: diagnosticTests
    });

  }, [diagnosticTests, activeDiagCodes, reportedMetrics]);

  // We throttle the diagnostics tests to not run too often for two reasons:
  // save system resources and to let users be able to see toggling alerts.
  // The latter should probably be handled in some other way though.
  useDebounceWithMaxDelay({
    callback: runDiagnosticsTests,
    delay: 500,
    maxDelay: 2000
  });

  //
  // Run periodic check that prints warning messages for whitelisted metrics
  // that are not recorded in the metrics registry. This is so developers can
  // see when they misspell metrics in their test predicates.
  // We wait with initial checks until the metrics polling has been through
  // a couple of iterations, to avoid false positives.
  //

  useInterval({
    callback: () => {
      const now = Date.now();
      const missingMetrics = [];

      for (const [metric, timestamp] of metricWhitelist) {
        if (!reportedMetrics.hasOwnProperty(metric) && (now - timestamp) > 5000) {
          missingMetrics.push(metric);
        }
      }

      if (missingMetrics.length !== 0) {
        console.warn("Missing metrics: ", missingMetrics.join(", "));
      }
    },
    delay: 10_000,   // The metric polling starts after ~5s and executes every 2s.
    interval: 5_000, // One possible warning every 5s
  });

  useEffect(() => {
      Debug.logGroup("Metric whitelist updated.", {Whitelist: metricWhitelist});
  }, [metricWhitelist]);

  //
  // Compile details from vehicle parameters once.
  // It is used to dynamically set up diagnostic tests.
  //

  const compressVehicleParameters = ({parameters_values}) => {
    const { lidarArray, radarArray, cameraArray } = parameters_values;
    return {
      lidars: lidarArray.map(sensor => ({ referenceFrame: sensor.sensorReferenceFrame })),
      radars: radarArray.map(sensor => ({ referenceFrame: sensor.sensorReferenceFrame })),
      cameras: cameraArray.map(sensor => ({ referenceFrame: sensor.sensorReferenceFrame })),
    };
  };

  const stopParameterPolling = useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_b, "parameters/file"),
    parser: ResponseParsers.JSON,
    handler: (parameters) => {
      if (parameters) {
        setVehicleParameters(compressVehicleParameters(parameters));
        stopParameterPolling();
      }
    },
    delay: 500,
    interval: 500,
  });

  useEffect(() => {
    if (vehicleParameters) {
      Debug.logGroup("Vehicle parameters loaded.", vehicleParameters);
      setDiagnosticTests(prev => [...prev, ...Diag.createSensorTests(vehicleParameters)]);
    }
  }, [vehicleParameters]);

  //
  // Poll PTPD timesync issues periodically.
  //

  useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_b, "timesync/ptpd_status"),
    parser: ResponseParsers.JSON,
    handler: (statuses) => {
      updateRestPollTimouts("pcu_b");
      updateReportedMetrics(Diag.compilePtpdTimesyncMetrics(statuses));
    },
    delay: 3300,
    interval: 2000,
  });

  //
  // Poll monit system usage periodically.
  // We use this state for system utilization metrics.
  //

  const updateSystemUsageMetrics = (data, host) => {
    updateRestPollTimouts(host);
    updateReportedMetrics({
      ["cpu_percent_" + host]: data["cpu_percent"],
      ["ram_percent_" + host]: data["ram_percent"],
    });
  }

  useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_b, "monit/system_usage"),
    parser: ResponseParsers.JSON,
    handler: (data) => updateSystemUsageMetrics(data, "pcu_b"),
    delay: 3100,
    interval: 4000,
  });

  useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_a, "monit/system_usage"),
    parser: ResponseParsers.JSON,
    handler: (data) => updateSystemUsageMetrics(data, "pcu_a"),
    delay: 5100,
    interval: 4000,
  });

  //
  // Poll monit summary periodically.
  // We use this state for process status metrics.
  //

  // NOTE(abezqk, 2026-06-20):
  //  Note that there is some inconsistency between what the PCU's report,
  //  and none is a complete subset of the other. E.g. all osdc processes
  //  are reported on PCU-A, with only osdi-Lidar3 and 4. These are
  //  reported by PCU-B. However, PCU-B does not report osdc-Lidar3 and 4.
  //  So we have to poll both and join them. I assume that the duplicates
  //  match and implement no logic to conditionally update the metrics.

  const updateProcessStatusMetrics = (summary, host) => {
    const makeEntry = ([key, value]) => [ "process_status_" + key, value.status ];
    const statuses = Object.fromEntries(Object.entries(summary).map(makeEntry));
    updateRestPollTimouts(host);
    updateReportedMetrics(statuses);
  }

  useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_b, "monit/summary"),
    parser: ResponseParsers.JSON,
    handler: (summary) => updateProcessStatusMetrics(summary, "pcu_b"),
    delay: 3200,
    interval: 5000,
  });

  useRestPolling({
    endpoint: adptRestEndpoint(config?.ats_adpt_rest.pcu_a, "monit/summary"),
    parser: ResponseParsers.JSON,
    handler: (summary) => updateProcessStatusMetrics(summary, "pcu_a"),
    delay: 4200,
    interval: 5000,
  });

  //
  // Toast messages
  //

  const loggerConnected = socketIsConnected && !socketLostConnection;

  const waitingForDiagnostics = loggerConnected && ( activeDiagCodes === null );

  const waitingForParameters = loggerConnected && !vehicleParameters;

  const unresponsiveRestApi =
    !waitingForDiagnostics &&
    !waitingForParameters &&
    (
      5000 < (Date.now() - lastSuccessfulRestPoll.pcu_a) ||
      5000 < (Date.now() - lastSuccessfulRestPoll.pcu_b)
    );

  const toastMessages = [
    configLoading && !configError && "Loading configuration...",
    configError && `Loading configuration... ${configError}`,
    !socketIsConnected && "Connecting to logging server...",
    socketLostConnection && "Lost connection to logging server... Retrying...",
    waitingForDiagnostics && "Waiting for diagnostics...",
    waitingForParameters && "Collecting vehicle parameters...",
    unresponsiveRestApi && "Lost connection to diagnostics server... Retrying...",
  ].filter(Boolean);

  //
  // JSX Component
  //

  return (
    <div className="flex bg-grey-928">
      <UserContext.Provider
        value={{
          timer,
          setTimer,
          buttonstr,
          setButtonstr,
          isrecording,
          setRecordingStatus,
          id,
          setIntervalId,
        }}
      >
        <Header messageDatalength={messageDataLength} />
        <Routes>
          <Route
            path="/"
            element={
              <MainView
                toastMessages={toastMessages}
                socket={socket}
                alertLength={messageDataLength}
                diskdata={disk}
                cameraStatusMessage={cameraStatusMessage}
                lidarStatus={lidarStatus}
                videoInputStatus={videoInputStatus}
              />
            }
          />
          <Route
            path="/Alerts"
            element={
              <AlertView
                toastMessages={toastMessages}
                alertMessages={alertMessages}
              />
            }
          />
          <Route
            path="/status"
            element={
              <StatusView
                videoInputStatus={videoInputStatus}
                lidarStatus={lidarStatus}
              />
            }
          />
        </Routes>
      </UserContext.Provider>
    </div>
  );
}

export default App;
